import type { FavoriteView } from '@/api/favorites'
import { VpButton, VpIcon } from '@vtmn-play/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FavoriteViewManagementMenu } from '@/components/FavoriteViews/FavoriteViewManagementMenu'
import { FavoriteViewTitleEditForm } from '@/components/FavoriteViews/FavoriteViewTitleEditForm'
import './favorite-view-card.scss'

interface FavoriteViewCardProps {
  view: FavoriteView
  countryLabel: string
  onOpen: () => void
  onRename: (title: string) => Promise<boolean>
  onDelete: () => void
  onShare: () => void
}

export function FavoriteViewCard({ view, countryLabel, onOpen, onRename, onDelete, onShare }: Readonly<FavoriteViewCardProps>) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const titleButtonRef = useRef<HTMLButtonElement>(null)
  const shouldRestoreTitleFocusRef = useRef(false)

  const closeTitleEdit = useCallback((restoreFocus: boolean) => {
    shouldRestoreTitleFocusRef.current = restoreFocus
    setIsEditing(false)
  }, [])

  useEffect(() => {
    if (shouldRestoreTitleFocusRef.current) {
      titleButtonRef.current?.focus()
      shouldRestoreTitleFocusRef.current = false
    }
  }, [isEditing])

  const image = view.previewImageUrl
    ? <img src={view.previewImageUrl} alt="" loading="lazy" data-testid={`favorite-views-preview-${view.id}`} />
    : <VpIcon name="image" />

  return (
    <li className="favorite-views-page__view-card">
      <VpButton
        variant="tertiary"
        className="favorite-views-page__view-open"
        aria-label={`${t('favorite-views.page.open-view')}: ${view.title}`}
        onClick={onOpen}
      >
        <span className="favorite-views-page__view-image">{image}</span>
      </VpButton>
      <FavoriteViewManagementMenu
        accessibleName={t('favorite-views.management.manage-view-named', { name: view.title })}
        dialogType="view"
        menuId={`favorite-view-menu-${view.id}`}
        menuTestId={`favorite-views-menu-${view.id}`}
        onRename={() => {
          setIsEditing(true)
        }}
        onDelete={onDelete}
        onShare={onShare}
        renameTestId={`favorite-views-rename-view-${view.id}`}
        deleteTestId={`favorite-views-delete-view-${view.id}`}
        shareTestId={`favorite-views-share-view-${view.id}`}
        triggerTestId={`favorite-views-menu-button-${view.id}`}
      />
      <div className="favorite-views-page__view-card-title">
        {isEditing
          ? (
              <FavoriteViewTitleEditForm
                viewId={view.id}
                title={view.title}
                onRename={onRename}
                onClose={closeTitleEdit}
              />
            )
          : (
              <VpButton ref={titleButtonRef} variant="tertiary" className="favorite-views-page__view-title" onClick={onOpen}>
                {view.title}
              </VpButton>
            )}
      </div>
      <dl className="favorite-views-page__metadata favorite-views-page__metadata--compact">
        <div>
          <VpIcon name="price-tag" />
          <dt>{t('favorite-views.page.supermodels')}</dt>
          <dd>{t('favorite-views.page.supermodel-count', { count: view.supermodelCount ?? 0 })}</dd>
        </div>
        <div>
          <VpIcon name="calendar" />
          <dt>{t('favorite-views.page.season')}</dt>
          <dd>{view.search.season}</dd>
        </div>
        <div>
          <VpIcon name="earth" />
          <dt>{t('favorite-views.page.countries')}</dt>
          <dd>{countryLabel}</dd>
        </div>
      </dl>
    </li>
  )
}
